import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { formatScore } from "./v2OverviewUtils";
import { MUSCLE_I18N_KEYS, zoneColor } from "./v2PriorityUtils";

const POPOVER_WIDTH = 280;
const OFFSET = 14;

export default function V2MuscleDetailModal({ detail, onClose }) {
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [coords, setCoords] = useState({ left: 0, top: 0 });

  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  useLayoutEffect(() => {
    if (!detail) return;
    const width = cardRef.current?.offsetWidth || POPOVER_WIDTH;
    const height = cardRef.current?.offsetHeight || 180;
    const left = Math.min(Math.max(12, (detail.x ?? 0) + OFFSET), window.innerWidth - width - 12);
    const top = Math.min(Math.max(12, (detail.y ?? 0) + OFFSET), window.innerHeight - height - 12);
    setCoords({ left, top });
  }, [detail]);

  if (!detail) return null;

  const muscleKey = MUSCLE_I18N_KEYS[detail.muscle] || detail.muscle;
  const title = `${t(`muscles.${muscleKey}`, { defaultValue: detail.muscle })} · ${t(`v2.overview.tooltip.side_${detail.side}`)}`;

  return (
    <div className="fixed inset-0 z-[80]" onClick={onClose} role="presentation">
      <div
        ref={cardRef}
        className="absolute w-[280px] rounded-xl border border-[rgba(98,94,166,0.5)] bg-[#19144e] p-3.5 shadow-[0_16px_40px_rgba(2,0,16,0.55)]"
        style={{ left: coords.left, top: coords.top }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="v2-muscle-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="m-0 text-[10px] font-bold uppercase tracking-[0.12em] text-[#34cbff]">{detail.playerName}</p>
            <h3 className="mt-1 text-[16px] leading-tight text-[#f6f3ff]" id="v2-muscle-modal-title">{title}</h3>
          </div>
          <button
            type="button"
            className="grid h-7 w-7 shrink-0 place-items-center rounded-full border-0 bg-[rgba(255,255,255,0.08)] text-[#f6f3ff]"
            onClick={onClose}
            aria-label={t("fbx_viewer.close_muscle")}
          >
            <X size={14} />
          </button>
        </header>

        <div className="mt-3 grid grid-cols-3 gap-2">
          <div>
            <span className="block text-[10px] uppercase tracking-wide text-[#9b96c9]">{t("v2.overview.trainability")}</span>
            <strong className="v2-display mt-0.5 block text-[22px] leading-none" style={{ color: zoneColor(detail.zone) }}>
              {formatScore(detail.score)}
              <small className="text-[12px] text-[#8f8abf]">/10</small>
            </strong>
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-wide text-[#9b96c9]">{t("v2.overview.priority.zone")}</span>
            <em className="v2-display mt-0.5 block text-[18px] not-italic leading-tight" style={{ color: zoneColor(detail.zone) }}>
              {t(`v2.overview.${detail.zone}`)}
            </em>
          </div>
          <div>
            <span className="block text-[10px] uppercase tracking-wide text-[#9b96c9]">{t("v2.overview.priority.window")}</span>
            <strong className="mt-0.5 block text-[12px] leading-tight text-[#f6f3ff]">
              {t(`v2.overview.priority.period_${detail.period}`)}
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
}
